import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Box } from "@chakra-ui/react";
import { useFocus } from "src/hooks/useElementFocus";

const animateNext = {
  initial: { x: "25%", opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: "-25%", opacity: 0 }
};

const animatePrevious = {
  initial: { x: "-25%", opacity: 0 },
  animate: { x: 0, opacity: 1 },
  exit: { x: "25%", opacity: 0 }
};

const variants = {
  next: animateNext,
  previous: animatePrevious
};

export default function Flow({ steps, initialStep, ...rest }) {
  const focus = useFocus();
  const _initialStep = useRef(initialStep || steps[0].id).current;
  const [currentStep, setCurrentStep] = useState(_initialStep);
  const [direction, setDirection] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [state, setState] = useState({});
  const [history, setHistory] = useState([_initialStep]);
  const que = useRef([]);
  const canContinue = useRef(true);

  const _steps = useRef(steps).current;
  const nextStepId = useRef(null);

  const Step = useMemo(
    () => _steps.find((step) => step.id === currentStep)?.Component,
    [_steps, currentStep]
  );
  const animationVariant = variants[direction] || null;

  const checkIfCanContinue = useCallback((callback) => {
    if (canContinue.current) return true;
    que.current.push(callback);
    return false;
  }, []);

  const handleContinue = useCallback(() => {
    const callback = que.current.shift();
    if (callback) callback();
  }, []);

  const updateState = useCallback((update = {}) => {
    setState((prev) => ({ ...prev, ...update }));
  }, []);
  const updateHistory = useCallback((update) => {
    setHistory((prev) => [update, ...prev]);
  }, []);
  const updateProvidingStore = useCallback(
    ({ state, history }) => {
      updateState(state);
      updateHistory(history);
    },
    [updateHistory, updateState]
  );

  const push = useCallback(
    (
      stepId,
      { direction: stepDirection = "next", state: stateUpdate = {} } = {}
    ) => {
      const canContinue = checkIfCanContinue(() =>
        push(stepId, { direction: stepDirection, state: stateUpdate })
      );
      if (!canContinue) return;
      updateProvidingStore({
        state: stateUpdate,
        history: stepId
      });
      nextStepId.current = stepId;
      setDirection(stepDirection);
      setIsAnimating(true);
    },
    [updateProvidingStore, checkIfCanContinue]
  );

  const next = useCallback(
    ({ state: stateUpdate = {} } = {}) => {
      const currentIndex = _steps.findIndex((step) => step.id === currentStep);
      const nextStep = _steps[currentIndex + 1];
      if (nextStep) {
        updateProvidingStore({
          state: stateUpdate,
          history: nextStep.id
        });

        nextStepId.current = nextStep.id;
        setDirection("next");
        setIsAnimating(true);
      }
    },
    [_steps, currentStep, updateProvidingStore]
  );

  const previous = useCallback(
    ({ state: stateUpdate = {} } = {}) => {
      const currentIndex = _steps.findIndex((step) => step.id === currentStep);
      const _previousStepId = history[1];
      const previousStepFallback = _steps[currentIndex - 1];

      const previousStep = _previousStepId || previousStepFallback?.id;

      if (previousStep) {
        updateProvidingStore({
          state: stateUpdate,
          history: previousStep
        });

        nextStepId.current = previousStep;
        setDirection("previous");
        setIsAnimating(true);
      }
    },
    [_steps, currentStep, history, updateProvidingStore]
  );

  const backToFirstStep = useCallback(
    ({ state: stateUpdate = {} }) => {
      updateProvidingStore({
        state: stateUpdate,
        history: _initialStep
      });
      setCurrentStep(_initialStep);
    },
    [_initialStep, updateProvidingStore]
  );

  useEffect(() => {
    if (isAnimating && nextStepId.current) {
      setCurrentStep(nextStepId.current);
      setIsAnimating(false);
    }
  }, [isAnimating]);

  const flowProviding = useMemo(() => {
    return {
      push,
      next,
      previous,
      backToFirstStep,
      setState: updateState,
      state,
      history
    };
  }, [push, next, previous, backToFirstStep, updateState, state, history]);

  return (
    <Box position="relative" w="100%" h="100%" overflowX="hidden">
      <AnimatePresence initial={false} mode="wait">
        <motion.div
          layoutId={`step-${currentStep}`}
          key={currentStep}
          variants={animationVariant}
          initial="initial"
          animate="animate"
          exit="exit"
          transition={{ duration: 0.2, ease: "easeInOut" }}
          onAnimationStart={(e) => {
            focus.beforeNavigation(e);
            canContinue.current = false;
          }}
          onAnimationComplete={(e) => {
            focus.afterNavigation(e);
            canContinue.current = true;
            handleContinue();
          }}
          style={{
            width: "100%",
            height: "100%",
            position: "absolute"
          }}
        >
          {(Step && (
            <Step
              key={currentStep}
              push={push}
              next={next}
              previous={previous}
              backToFirstStep={backToFirstStep}
              flow={flowProviding}
              initialStep={initialStep}
              {...rest}
            />
          )) ||
            null}
        </motion.div>
      </AnimatePresence>
    </Box>
  );
}
